import { apiRequest } from '@/lib/queryClient';
import type { FigureComposition, FigurePanel, PanelAnnotation, JournalTemplateId } from '@/store/figure-composition';

/**
 * Payload type for creating/updating figure compositions
 */
export interface FigureCompositionPayload {
  title: string;
  caption: string;
  templatePresetId?: string | null;
  panels: FigurePanel[];
  canvasSize: { width: number; height: number; unit: 'mm' };
  grid: { cols: number; rowHeight: number; margin: [number, number] };
  theme: {
    fontFamily: string;
    fontSize: number;
    colorMode: 'RGB' | 'CMYK';
    journalTemplate?: JournalTemplateId | null;
    stylePreset?: 'default' | 'nature-like' | 'plos-like' | 'clean-minimal' | 'custom' | null;
    colorblindSafe?: boolean | null;
  };
  axesSharing?: {
    shareXAcrossColumns: boolean;
    shareXAcrossAll: boolean;
    shareYAcrossRows: boolean;
    warnOnScaleMismatch: boolean;
    standardizeXTitles?: boolean;
    standardizeYTitles?: boolean;
    standardizeXScales?: boolean;
    standardizeYScales?: boolean;
  } | null;
  figureLegend?: {
    enabled: boolean;
    panelId?: string;
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  } | null;
}

/**
 * Response DTO from backend API (matches backend FigureCompositionDto)
 */
export interface FigureCompositionResponseDto {
  id: string;
  projectId: string;
  title: string;
  caption: string;
  templatePresetId?: string | null;
  panels: FigurePanel[];
  canvasSize: {
    width: number;
    height: number;
    unit: 'mm';
  };
  grid: {
    cols: number;
    rowHeight: number;
    margin: [number, number];
  };
  theme: {
    fontFamily: string;
    fontSize: number;
    colorMode: 'RGB' | 'CMYK';
    journalTemplate?: JournalTemplateId | null;
    stylePreset?: 'default' | 'nature-like' | 'plos-like' | 'clean-minimal' | 'custom' | null;
    colorblindSafe?: boolean | null;
  };
  axesSharing?: {
    shareXAcrossColumns: boolean;
    shareXAcrossAll: boolean;
    shareYAcrossRows: boolean;
    warnOnScaleMismatch: boolean;
    standardizeXTitles?: boolean;
    standardizeYTitles?: boolean;
    standardizeXScales?: boolean;
    standardizeYScales?: boolean;
  } | null;
  figureLegend?: {
    enabled: boolean;
    panelId?: string;
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  } | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Convert frontend FigureComposition to API payload (strips client-only fields)
 */
function compositionToPayload(comp: FigureComposition): FigureCompositionPayload {
  // Convert fontScale to fontSize if fontSize is not present
  // Base font size is 8pt, so fontSize = 8 * fontScale
  const baseFontSize = 8;
  const fontSize = comp.theme.fontSize ?? (comp.theme.fontScale ? Math.round(baseFontSize * comp.theme.fontScale) : baseFontSize);
  
  return {
    title: comp.title,
    caption: comp.caption,
    templatePresetId: comp.templatePresetId ?? null,
    panels: comp.panels,
    canvasSize: comp.canvasSize,
    grid: comp.grid,
    theme: {
      fontFamily: comp.theme.fontFamily || 'Arial', // Ensure fontFamily is never empty
      fontSize: fontSize,
      colorMode: comp.theme.colorMode || 'RGB', // Ensure colorMode is set
      journalTemplate: comp.theme.journalTemplate ?? null,
      stylePreset: comp.theme.stylePreset ?? null,
      colorblindSafe: comp.theme.colorblindSafe ?? null,
    },
    axesSharing: comp.axesSharing ?? null,
    figureLegend: comp.figureLegend ?? null,
  };
}

/**
 * Safely parse a date string, falling back to current date if invalid
 */
function parseDate(dateStr: unknown): Date {
  if (typeof dateStr !== 'string') {
    console.warn('Invalid date string, using current date:', dateStr);
    return new Date();
  }
  const parsed = new Date(dateStr);
  if (isNaN(parsed.getTime())) {
    console.warn('Invalid date string, using current date:', dateStr);
    return new Date();
  }
  return parsed;
}

/**
 * Convert API response to frontend FigureComposition (parses dates)
 */
function payloadToComposition(data: FigureCompositionResponseDto): FigureComposition {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid API response: expected object');
  }
  
  if (!data.id || !data.projectId) {
    throw new Error('Invalid API response: missing required fields (id, projectId)');
  }
  
  return {
    id: String(data.id),
    projectId: String(data.projectId),
    title: data.title ?? '',
    caption: data.caption ?? '',
    templatePresetId: data.templatePresetId ?? undefined,
    panels: Array.isArray(data.panels) ? data.panels : [],
    canvasSize: data.canvasSize ?? { width: 180, height: 215, unit: 'mm' },
    grid: data.grid ?? { cols: 12, rowHeight: 40, margin: [8, 8] },
    theme: {
      fontFamily: data.theme?.fontFamily ?? 'Arial',
      fontSize: data.theme?.fontSize ?? 8,
      colorMode: data.theme?.colorMode ?? 'RGB',
      journalTemplate: data.theme?.journalTemplate ?? undefined,
      stylePreset: data.theme?.stylePreset ?? undefined,
      colorblindSafe: data.theme?.colorblindSafe ?? undefined,
    },
    axesSharing: data.axesSharing ?? undefined,
    figureLegend: data.figureLegend ?? undefined,
    createdAt: parseDate(data.createdAt),
    updatedAt: parseDate(data.updatedAt),
  };
}

/**
 * Create a new figure composition
 */
export async function createFigureComposition(
  projectId: string,
  payload: FigureCompositionPayload
): Promise<FigureComposition> {
  const res = await apiRequest('POST', `/api/projects/${projectId}/figure-compositions`, payload);
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Failed to create composition' }));
    throw new Error(errorData.error || `HTTP ${res.status}: Failed to create composition`);
  }
  const data: FigureCompositionResponseDto = await res.json();
  return payloadToComposition(data);
}

/**
 * List all figure compositions for a project
 */
export async function listFigureCompositions(projectId: string): Promise<FigureComposition[]> {
  const res = await apiRequest('GET', `/api/projects/${projectId}/figure-compositions`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Failed to list compositions' }));
    throw new Error(errorData.error || `HTTP ${res.status}: Failed to list compositions`);
  }
  const data: FigureCompositionResponseDto[] = await res.json();
  return Array.isArray(data) ? data.map(payloadToComposition) : [];
}

/**
 * Get a single figure composition by ID
 */
export async function getFigureComposition(id: string): Promise<FigureComposition> {
  const res = await apiRequest('GET', `/api/figure-compositions/${id}`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Failed to get composition' }));
    throw new Error(errorData.error || `HTTP ${res.status}: Failed to get composition`);
  }
  const data: FigureCompositionResponseDto = await res.json();
  return payloadToComposition(data);
}

/**
 * Update a figure composition
 */
export async function updateFigureComposition(
  id: string,
  payload: FigureCompositionPayload
): Promise<FigureComposition> {
  const res = await apiRequest('PUT', `/api/figure-compositions/${id}`, payload);
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Failed to update composition' }));
    throw new Error(errorData.error || `HTTP ${res.status}: Failed to update composition`);
  }
  const data: FigureCompositionResponseDto = await res.json();
  return payloadToComposition(data);
}

/**
 * Delete a figure composition
 */
export async function deleteFigureComposition(id: string): Promise<void> {
  const res = await apiRequest('DELETE', `/api/figure-compositions/${id}`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Failed to delete composition' }));
    throw new Error(errorData.error || `HTTP ${res.status}: Failed to delete composition`);
  }
}

/**
 * Helper to convert a store FigureComposition to payload for API calls
 */
export function compositionToApiPayload(comp: FigureComposition): FigureCompositionPayload {
  return compositionToPayload(comp);
}

